/**
 * Rebuild ReportActivitySet[] from a filed-report log snapshot
 * so /logs full-screen preview matches the composer set layout.
 */
import {
  descriptionBlocksFromPayload,
  createInitialDescriptionBlocks,
  type ReportDescriptionBlock,
} from 'pages/Reporting/reportDescriptionBlocks';
import {
  createReportActivitySet,
  type ReportActivitySet,
  type ReportActivitySetRow,
} from 'pages/Reporting/reportActivitySets';
import {
  sanitizeReportContextUsers,
  type ReportContextUser,
} from 'pages/Reporting/reportContextUsers';
import { sanitizeAlertLevelSummary } from 'pages/Reporting/reportActivityLogs';
import { parseActivityLogsTypeAttributeModeParam } from './logUtils';

export type FiledReportPreviewSnapshot = {
  subject?: string,
  descriptionBlocks?: unknown,
  reportSections?: {
    serial?: number,
    descriptionBlocks?: unknown,
    combineLogTypes?: boolean,
    sourceIds?: unknown[],
    valueMode?: 'value' | 'units',
    typeAttributeMode?: string,
  }[],
  referencedActivityLogs?: {
    sourceId?: string,
    type?: string,
    message?: string,
    date?: string,
    value?: string,
    units?: string,
    valueMode?: 'value' | 'units',
    tmpId?: number,
  }[],
  metricsBySet?: {
    serial?: number,
    chartsLabel?: string,
    logCount?: number,
  }[],
  showCharts?: boolean,
  /** Compliance people saved when the report was filed. */
  contextUsers?: unknown,
  alertLevelSummary?: string,
};

type FiledRefRow = {
  sourceId: string,
  type: string,
  message: string,
  date?: string,
  value?: string,
  units?: string,
  valueMode: 'value' | 'units',
  tmpId: number,
};

const refsFromSnapshot = (preview: FiledReportPreviewSnapshot): FiledRefRow[] => {
  const raw = Array.isArray(preview.referencedActivityLogs)
    ? preview.referencedActivityLogs
    : [];
  return raw.map((row, index) => {
    const parsedTmp = Number(row?.tmpId);
    return {
      sourceId: String(row?.sourceId || '').trim(),
      type: String(row?.type || 'Activity').trim() || 'Activity',
      message: String(row?.message || ''),
      date: row?.date ? String(row.date) : undefined,
      value: row?.value != null ? String(row.value) : undefined,
      units: row?.units != null ? String(row.units) : undefined,
      valueMode: row?.valueMode === 'units' ? 'units' as const : 'value' as const,
      tmpId: Number.isFinite(parsedTmp) && parsedTmp > 0
        ? Math.floor(parsedTmp)
        : index + 1,
    };
  });
};

const rowsFromRefs = (refs: FiledRefRow[]): ReportActivitySetRow[] => (
  refs.map((r, i) => ({
    sourceId: r.sourceId,
    tmpId: r.tmpId > 0 ? r.tmpId : i + 1,
    type: r.type,
    message: r.message,
    date: r.date,
    value: r.value,
    units: r.units,
  }))
);

/** Pick rows for one section — sourceIds first, else metrics logCount slice. */
const sectionRows = (
  section: NonNullable<FiledReportPreviewSnapshot['reportSections']>[number],
  sectionIndex: number,
  allSections: NonNullable<FiledReportPreviewSnapshot['reportSections']>,
  refs: FiledRefRow[],
  metricsBySet: NonNullable<FiledReportPreviewSnapshot['metricsBySet']>,
): FiledRefRow[] => {
  const sourceIds = Array.isArray(section.sourceIds)
    ? section.sourceIds.map((id) => String(id || '').trim()).filter(Boolean)
    : [];
  if (sourceIds.length) {
    const want = new Set(sourceIds);
    const matched = refs.filter((r) => r.sourceId && want.has(r.sourceId));
    if (matched.length) return matched;
  }

  if (metricsBySet.length && refs.length) {
    let offset = 0;
    for (let i = 0; i < metricsBySet.length; i += 1) {
      const count = Math.max(0, Math.floor(Number(metricsBySet[i].logCount) || 0));
      const serial = Number(metricsBySet[i].serial) > 0
        ? Math.floor(Number(metricsBySet[i].serial))
        : i + 1;
      const sectionSerial = Number(section.serial) > 0
        ? Math.floor(Number(section.serial))
        : sectionIndex + 1;
      const slice = refs.slice(offset, offset + count);
      offset += count;
      if (serial === sectionSerial) return slice;
    }
  }

  // Single-set / legacy: all referenced rows belong to the only section.
  if (allSections.length === 1) return refs;
  return [];
};

/**
 * Rebuild composer activity sets from the filed-log snapshot.
 * Empty when the snapshot has no per-set sections (flat description only).
 */
export const activitySetsFromFiledReportPreview = (
  preview: FiledReportPreviewSnapshot | null | undefined,
): ReportActivitySet[] => {
  if (!preview || typeof preview !== 'object') return [];
  const sections = Array.isArray(preview.reportSections) ? preview.reportSections : [];
  if (!sections.length) return [];

  const refs = refsFromSnapshot(preview);
  const metricsBySet = Array.isArray(preview.metricsBySet) ? preview.metricsBySet : [];
  const showChartsGlobal = preview.showCharts !== false;
  const chartsLabelBySerial = new Map(
    metricsBySet.map((m, i) => {
      const serial = Number(m.serial) > 0 ? Math.floor(Number(m.serial)) : i + 1;
      const label = typeof m.chartsLabel === 'string' ? m.chartsLabel.trim() : '';
      return [serial, label] as const;
    }),
  );

  return sections.map((section, index) => {
    const serial = Number(section.serial) > 0
      ? Math.floor(Number(section.serial))
      : index + 1;
    const descBlocks = descriptionBlocksFromPayload(section.descriptionBlocks);
    const setRefs = sectionRows(section, index, sections, refs, metricsBySet);
    const valueMode = section.valueMode === 'units' ? 'units' as const : 'value' as const;
    const chartsTitle = chartsLabelBySerial.get(serial) || '';
    // No metrics entry with charts → still show when global showCharts and set has rows.
    const setHasMetrics = metricsBySet.some((m) => {
      const s = Number(m.serial) > 0 ? Math.floor(Number(m.serial)) : 0;
      return s === serial && Number(m.logCount) > 0;
    });
    const showCharts = showChartsGlobal && (setHasMetrics || setRefs.length > 0);

    return createReportActivitySet(
      rowsFromRefs(setRefs),
      serial,
      descBlocks,
      showCharts,
      valueMode,
      chartsTitle,
      section.combineLogTypes === true,
      parseActivityLogsTypeAttributeModeParam(section.typeAttributeMode),
    );
  });
};

export const filedReportPreviewBody = (
  preview: FiledReportPreviewSnapshot | null | undefined,
): {
  subject: string,
  descBlocks: ReportDescriptionBlock[],
  activitySets: ReportActivitySet[],
  contextUsers: ReportContextUser[],
  alertLevelSummary: string,
} | null => {
  if (!preview || typeof preview !== 'object') return null;
  const subject = String(preview.subject || '').trim();
  const activitySets = activitySetsFromFiledReportPreview(preview);
  const descBlocks = activitySets.length
    ? createInitialDescriptionBlocks()
    : descriptionBlocksFromPayload(preview.descriptionBlocks);
  const contextUsers = sanitizeReportContextUsers(preview.contextUsers);
  const alertLevelSummary = sanitizeAlertLevelSummary(preview.alertLevelSummary);
  const hasBody = activitySets.length
    ? activitySets.some((set) => (set.descBlocks || []).some((b) => (
      (b.kind === 'text' && String(b.text || '').trim())
      || b.kind === 'log'
      || b.kind === 'picture'
    )))
    : descBlocks.some((b) => (
      (b.kind === 'text' && String(b.text || '').trim())
      || b.kind === 'log'
      || b.kind === 'picture'
    ));
  const hasCompliance = contextUsers.length > 0 || Boolean(alertLevelSummary);
  if (!subject && !hasBody && !hasCompliance) return null;
  return {
    subject,
    descBlocks,
    activitySets,
    contextUsers,
    alertLevelSummary,
  };
};
